import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  approveBooking,
  listAdminBookings,
  rejectBooking,
  type BookingStatus,
  type PublicBooking,
} from '../../lib/facilities-api';
import { extractApiError } from '../../lib/api-client';
import { listAdminUnits } from '../../lib/units-api';
import { listResidents } from '../../lib/residents-api';
import { formatDateTime } from '../../lib/format';
import { BookingStatusChip, BOOKING_STATUS_KEY } from './status-chip';
import { useT } from '../../i18n';

const STATUS_OPTIONS: ReadonlyArray<BookingStatus> = [
  'PENDING_APPROVAL',
  'CONFIRMED',
  'CANCELLED',
  'REJECTED',
  'COMPLETED',
];

type StatusFilter = BookingStatus | 'ALL';

const PAGE_SIZE = 50;

export function BookingsListPage() {
  const t = useT();
  const [items, setItems] = useState<PublicBooking[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING_APPROVAL');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PublicBooking | null>(null);
  // Bookings carry only ids; map them to the house label ("A 1") and the
  // booker's name for display. Each lookup is optional: without access to
  // units or residents the column falls back to what it can show.
  const [unitLabels, setUnitLabels] = useState<Map<string, string>>(new Map());
  const [bookerNames, setBookerNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    listAdminUnits()
      .then((units) => setUnitLabels(new Map(units.map((u) => [u.id, u.label]))))
      .catch(() => {});
    listResidents()
      .then((rs) => setBookerNames(new Map(rs.map((r) => [r.id, r.full_name]))))
      .catch(() => {});
  }, []);

  async function loadFirstPage(filter: StatusFilter) {
    setLoading(true);
    setError(null);
    try {
      const out = await listAdminBookings({
        limit: PAGE_SIZE,
        ...(filter !== 'ALL' ? { status: filter } : {}),
      });
      setItems(out.items);
      setNextCursor(out.nextCursor);
    } catch (e) {
      setError(extractApiError(e).message);
      setItems([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    setError(null);
    try {
      const out = await listAdminBookings({
        limit: PAGE_SIZE,
        cursor: nextCursor,
        ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
      });
      setItems((prev) => [...prev, ...out.items]);
      setNextCursor(out.nextCursor);
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    loadFirstPage(statusFilter);
  }, [statusFilter]);

  async function handleApprove(b: PublicBooking) {
    setBusyId(b.id);
    setError(null);
    try {
      await approveBooking(b.id);
      setFlash(t('book.approvedMsg', { facility: b.facility?.name ?? 'facility' }));
      await loadFirstPage(statusFilter);
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <div>
          <Typography variant="h4" fontWeight={600}>
            {t('book.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('book.subtitle')}
          </Typography>
        </div>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="booking-status-filter">{t('common.status')}</InputLabel>
          <Select
            labelId="booking-status-filter"
            label={t('common.status')}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <MenuItem value="ALL">{t('common.all')}</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {t(BOOKING_STATUS_KEY[s] ?? s)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {flash ? (
        <Alert severity="success" onClose={() => setFlash(null)}>
          {flash}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {statusFilter === 'ALL'
                  ? t('book.emptyAll')
                  : t('book.emptyStatus', { status: t(BOOKING_STATUS_KEY[statusFilter]) })}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('book.colFacility')}</TableCell>
                      <TableCell sx={{ width: 220 }}>{t('book.colWhen')}</TableCell>
                      <TableCell sx={{ width: 200 }}>{t('book.colUnit')}</TableCell>
                      <TableCell sx={{ width: 130 }}>{t('common.status')}</TableCell>
                      <TableCell align="right" sx={{ width: 180 }}>
                        {t('common.actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((b) => {
                      const pending = b.status === 'PENDING_APPROVAL';
                      const isBusy = busyId === b.id;
                      return (
                        <TableRow key={b.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {b.facility?.name ?? '(unknown)'}
                            </Typography>
                            {b.notes ? (
                              <Typography variant="caption" color="text.secondary">
                                {b.notes.slice(0, 80)}
                                {b.notes.length > 80 ? '…' : ''}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateTime(b.start_at)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              → {formatDateTime(b.end_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {unitLabels.has(b.unit_id) ? (
                              <Typography variant="body2">{unitLabels.get(b.unit_id)}</Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {b.unit_id.slice(0, 8)}
                              </Typography>
                            )}
                            {bookerNames.has(b.user_id) ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {bookerNames.get(b.user_id)}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <BookingStatusChip status={b.status} />
                            {b.rejected_reason ? (
                              <Typography variant="caption" color="error" display="block">
                                {b.rejected_reason}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">
                            {pending ? (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  disabled={isBusy}
                                  onClick={() => handleApprove(b)}
                                >
                                  {t('book.approve')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={isBusy}
                                  onClick={() => setRejectTarget(b)}
                                >
                                  {t('book.reject')}
                                </Button>
                              </Stack>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {nextCursor ? (
                <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                  <Button onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? t('common.loadingEllipsis') : t('common.loadMore')}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <RejectDialog
        target={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onDone={async (msg) => {
          setRejectTarget(null);
          setFlash(msg);
          await loadFirstPage(statusFilter);
        }}
      />
    </Stack>
  );
}

function RejectDialog(props: {
  target: PublicBooking | null;
  onClose: () => void;
  onDone: (message: string) => Promise<void>;
}) {
  const t = useT();
  const { target, onClose, onDone } = props;
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (target) {
      setReason('');
      setErr(null);
    }
  }, [target]);

  if (!target) return null;

  async function handleSubmit() {
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setErr(t('book.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await rejectBooking(target!.id, trimmed);
      await onDone(t('book.rejectedMsg', { facility: target!.facility?.name ?? 'facility' }));
    } catch (e) {
      setErr(extractApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={true} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('book.rejectTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {err ? <Alert severity="error">{err}</Alert> : null}
          <Alert severity="info">
            {t('book.rejectInfo')}
          </Alert>
          <TextField
            label={t('book.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            placeholder={t('book.reasonPlaceholder')}
            fullWidth
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={submitting}
        >
          {submitting ? t('book.rejecting') : t('book.rejectBtn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
